/**
 * DashboardPage - Group dashboard with metrics, device status, and recent activity
 */

import { Page, View, Model, Collection, TableView } from 'web-mojo';
import { MetricsChart, MetricsMiniChart, MetricsMiniChartWidget } from 'web-mojo/charts';
import { DeviceList } from '@portal/models/Device.js';
import { DeviceHistoryList } from '@portal/models/DeviceHistory.js';


class MetricsView extends View {
    constructor(options = {}) {
        super({ ...options, className: 'metrics-section' });
    }

    async getTemplate() {
        return `
        <div class="row g-3 mb-4">
            <div class="col-xl-4 col-lg-4 col-12">
                <div data-container="metric_users"></div>
            </div>
            <div class="col-xl-4 col-lg-4 col-12">
                <div data-container="metric_activity"></div>
            </div>
            <div class="col-xl-4 col-lg-4 col-12">
                <div data-container="metric_opens"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        const chartDefaults = {
            textColor: "#FFFFFF",
            granularity: 'days',
            trendRange: 4,
            trendOffset: 0,
            className: "shadow",
            account: 'group-',
            chartType: 'bar',
            showTooltip: true,
            showXAxis: true,
            height: 50,
            chartWidth: '100%',
            color: 'rgba(245, 245, 255, 0.8)',
            fill: true,
            fillColor: 'rgba(245, 245, 255, 0.6)',
            smoothing: 0.3,
            showTrending: true,
        };

        this.userChart = new MetricsMiniChartWidget({
            ...chartDefaults,
            icon: "bi bi-people fs-2",
            title: 'User Activity',
            subtitle: '{{now_value}}',
            background: "#5388D6",
            slugs: ['camaccess_member_activity_day'],
            containerId: 'metric_users'
        });
        this.addChild(this.userChart);

        this.activityChart = new MetricsMiniChartWidget({
            ...chartDefaults,
            icon: "bi bi-hdd-stack fs-2",
            title: 'Device Activity',
            subtitle: '{{now_value}}',
            background: "#38D1F2",
            slugs: ['camaccess_activity_day'],
            containerId: 'metric_activity'
        });
        this.addChild(this.activityChart);

        this.opensChart = new MetricsMiniChartWidget({
            ...chartDefaults,
            icon: "bi bi-unlock-fill fs-2",
            title: 'Lock Opens',
            subtitle: '{{now_value}}',
            background: "#7CA59B",
            slugs: ['camaccess_lock_open'],
            containerId: 'metric_opens'
        });
        this.addChild(this.opensChart);
    }

    async onAfterMount() {
        this.refreshValues();
    }

    async refreshValues() {
        const group = this.getApp().getActiveGroup();
        const account = `group-${group.id}`;
        [this.userChart, this.activityChart, this.opensChart].forEach(c => {
            c.account = account;
            if (c.chartOptions) c.chartOptions.account = account;
        });
        await Promise.allSettled([
            this.userChart?.refresh(),
            this.activityChart?.refresh(),
            this.opensChart?.refresh()
        ]);
    }
}


class DeviceStatusView extends View {
    constructor(options = {}) {
        super({ ...options, className: 'device-status-section' });
        this.devices = new DeviceList();
    }

    async getTemplate() {
        return `
        <style>
            .ds-card { background:#fff; border-radius:12px; box-shadow:0 1px 3px rgba(0,0,0,0.08); overflow:hidden; margin-bottom:1rem; }
            .ds-header { padding:1rem 1.25rem; border-bottom:1px solid #f0f0f0; display:flex; justify-content:space-between; align-items:center; }
            .ds-header h6 { margin:0; font-weight:700; font-size:0.95rem; }
            .ds-row { display:flex; align-items:center; padding:0.75rem 1.25rem; border-bottom:1px solid #f8f8f8; transition:background 0.15s; cursor:pointer; }
            .ds-row:hover { background:#f8f9fa; }
            .ds-row:last-child { border-bottom:none; }
            .ds-dot { width:8px; height:8px; border-radius:50%; margin-right:12px; flex-shrink:0; }
            .ds-dot.on { background:#28a745; box-shadow:0 0 6px rgba(40,167,69,0.4); }
            .ds-dot.off { background:#adb5bd; }
            .ds-info { flex:1; }
            .ds-info .name { font-weight:600; font-size:0.9rem; }
            .ds-info .location { font-size:0.75rem; color:#6c757d; }
            .ds-badge { font-size:0.7rem; padding:3px 10px; border-radius:10px; font-weight:700; }
            .ds-badge.locked { background:#fce4e4; color:#dc3545; }
            .ds-badge.unlocked { background:#d4edda; color:#28a745; }
            .ds-time { font-size:0.8rem; color:#6c757d; text-align:right; min-width:70px; }
            .ds-empty { padding:2rem; text-align:center; color:#adb5bd; }
        </style>
        <div class="ds-card">
            <div class="ds-header">
                <h6><i class="bi bi-hdd-stack me-2"></i>Devices</h6>
                <span style="font-size:0.8rem;color:#6c757d;" data-bind="device-count"></span>
            </div>
            <div data-bind="device-list"></div>
        </div>
        `;
    }

    async onAfterMount() {
        this.refresh();
    }

    async refresh() {
        const group = this.getApp().getActiveGroup();
        this.devices.params = { size: 50 };
        await this.devices.fetch();
        this._renderDevices();
    }

    _renderDevices() {
        const container = this.el.querySelector('[data-bind="device-list"]');
        const countEl = this.el.querySelector('[data-bind="device-count"]');
        if (!container) return;

        const models = this.devices.models || [];
        countEl.textContent = `${models.length} device${models.length !== 1 ? 's' : ''}`;

        if (models.length === 0) {
            container.innerHTML = '<div class="ds-empty">No devices found</div>';
            return;
        }

        container.innerHTML = models.map(d => {
            const data = d.attributes || d;
            const online = data.is_online;
            const locked = data.lock_engaged;
            const name = data.name || data.uuid;
            const location = data.location || '';
            const lastSeen = data.last_seen;
            const dotClass = online ? 'on' : 'off';
            const badgeClass = locked ? 'locked' : 'unlocked';
            const badgeIcon = locked ? 'bi-lock-fill' : 'bi-unlock-fill';
            const badgeText = locked ? 'Locked' : 'Open';

            let timeText = '';
            if (lastSeen) {
                try {
                    // Timestamps arrive as Unix epoch seconds (numeric or numeric string);
                    // ISO strings are also supported. Normalise to milliseconds, matching
                    // the framework's `relative` formatter (values < 1e11 are seconds).
                    let ms = typeof lastSeen === 'number' || /^\d+$/.test(String(lastSeen))
                        ? Number(lastSeen)
                        : new Date(lastSeen).getTime();
                    if (ms < 1e11) ms *= 1000;
                    const diff = Date.now() - ms;
                    const mins = Math.floor(diff / 60000);
                    if (Number.isNaN(mins)) timeText = '';
                    else if (mins < 1) timeText = 'just now';
                    else if (mins < 60) timeText = `${mins}m ago`;
                    else if (mins < 1440) timeText = `${Math.floor(mins/60)}h ago`;
                    else timeText = `${Math.floor(mins/1440)}d ago`;
                } catch(e) { timeText = ''; }
            }

            return `
            <div class="ds-row" data-action="view-device" data-device-id="${data.id}">
                <span class="ds-dot ${dotClass}"></span>
                <div class="ds-info">
                    <div class="name">${this._esc(name)}</div>
                    ${location ? `<div class="location">${this._esc(location)}</div>` : ''}
                </div>
                <div style="text-align:center;min-width:80px;">
                    <span class="ds-badge ${badgeClass}"><i class="bi ${badgeIcon} me-1"></i>${badgeText}</span>
                </div>
                <div class="ds-time"${!online && timeText ? ' style="color:#dc3545;"' : ''}>${timeText}</div>
            </div>`;
        }).join('');
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str;
        return d.innerHTML;
    }
}


export default class DeviceDashboardPage extends Page {
    constructor(options = {}) {
        super({
            ...options,
            title: 'Dashboard',
            icon: "bi-speedometer2",
            className: 'dashboard-page'
        });
        this.pageSubtitle = 'Monitoring and metrics overview';
    }

    async getTemplate() {
        return `
        <div class="dashboard-container container-lg">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <p class="text-muted mb-0">{{pageSubtitle}}</p>
                </div>
                <button class="btn btn-outline-secondary btn-sm" data-action="refresh-all">
                    <i class="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
            </div>

            <div data-container="metrics"></div>
            <div data-container="device-status"></div>

            <div style="background:#fff;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.08);overflow:hidden;">
                <div style="padding:1rem 1.25rem;border-bottom:1px solid #f0f0f0;">
                    <h6 style="margin:0;font-weight:700;font-size:0.95rem;">
                        <i class="bi bi-clock-history me-2"></i>Recent Activity
                    </h6>
                </div>
                <div data-container="recent-activity"></div>
            </div>
        </div>
        `;
    }

    async onInit() {
        this.metricsView = new MetricsView({ containerId: 'metrics' });
        this.addChild(this.metricsView);

        this.deviceStatus = new DeviceStatusView({ containerId: 'device-status' });
        this.addChild(this.deviceStatus);

        this.recentActivity = new TableView({
            containerId: "recent-activity",
            Collection: DeviceHistoryList,
            requiresActiveGroup: true,
            hideActivePillNames: ["device__group"],
            searchable: false,
            filterable: false,
            clickAction: "none",
            columns: [
                { label: "When", key: "created|relative" },
                { label: "Action", key: "action" },
                { label: "Source", key: "source" },
                { label: "User", key: "user" },
                { label: "Device", key: "device.name|default(model.device.uuid)" },
            ],
        });
        this.addChild(this.recentActivity);
    }

    async onEnter() {
        await super.onEnter();
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentActivity.collectionParams = {
            size: 10,
            device__group: this.activeGroup.id,
        };
    }

    async onActionRefreshAll() {
        await this.refreshDashboard();
        this.getApp().toast.success('Dashboard refreshed');
    }

    async onActionViewDevice(event, element) {
        const deviceId = element.dataset.deviceId;
        if (deviceId) {
            this.getApp().navigate(`?page=group/devices&id=${deviceId}`);
        }
    }

    async refreshDashboard() {
        this.activeGroup = this.getApp().getActiveGroup();
        this.recentActivity.collectionParams = {
            size: 10,
            device__group: this.activeGroup.id,
        };
        await Promise.allSettled([
            this.metricsView?.refreshValues(),
            this.deviceStatus?.refresh(),
            this.recentActivity?.collection?.fetch()
        ].filter(Boolean));
    }

    async onGroupChange(group) {
        this.refreshDashboard();
    }
}
